import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "../components/toast";
import type { ActionKey } from "../components/toast/ToastProvider";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { EditDrawer, type Dismiss } from "../components/EditDrawer";
import { isNendaAdminSession } from "../auth/audience";
import { placeName, type AccessDirectory } from "@shared/access/directory";
import type { HomeKind } from "@shared/access/homeKind";
import { isBelow, samePlace, type Place } from "@shared/access/places";
import type { RoleName } from "@shared/access/roles";
import {
  homeKindOfChoice,
  homePlace,
  type HomeChoice,
} from "@shared/access/userAccess";
import {
  foyerApi,
  type ContentOwnerRecord,
  type CreatedInviteRecord,
  type HomeInput,
  type InviteRecord,
  type UserRecord,
} from "../http/foyer";
import { errorCode, friendlyError } from "../utils/apiError";
import type { Translate } from "../i18n/translate";
import { useOptionalAuth } from "../auth/AuthContext";
import { PermissionGate, accessOf } from "../auth/access";
import type { Gate } from "../components/distribution/catalogueGates";
import {
  EMPTY_FORM,
  describeForm,
  formFromGrants,
  grantsFromForm,
  placeLabel,
  roleLabel,
  toWire,
  type GrantForm,
} from "./users/grantForm";
import {
  fitFormToHome,
  formProblems,
  homeChangeNote,
  inviteProblems,
  roleTooHigh,
} from "./users/formChecks";
import {
  GrantEditor,
  PlacesField,
  RoleSelect,
  placeOptions,
} from "./users/GrantEditor";
import { HomePicker } from "./users/HomePicker";

/** Lightweight email shape check for inline form validation. */
function isEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/** The server's reason for a refusal, when it gave one. */
function refusalMessage(
  t: Translate,
  action: ActionKey,
  e: unknown,
  params?: Record<string, string>
): string {
  const hint =
    typeof e === "object" && e !== null
      ? (e as { body?: { hint?: unknown } }).body?.hint
      : undefined;
  return typeof hint === "string" ? hint : describeError(t, action, e, params);
}

function toHomeInput(home: HomeChoice): HomeInput {
  return home.kind === "nenda" ? "nenda" : { companyId: home.companyId };
}

function kindOf(home: HomeChoice | null, directory: AccessDirectory): HomeKind {
  return home ? homeKindOfChoice(home, directory) : "customer";
}

function placeOfHome(
  home: HomeChoice | null,
  directory: AccessDirectory
): Place | null {
  return home ? homePlace(home, directory) : null;
}

function useCaller() {
  const me = useOptionalAuth()?.user ?? null;
  return {
    isSelf: (email: string) => !!me && me.user === email,
    isNendaAdmin: !me || isNendaAdminSession(me),
    isNendaStaff: !me || me.access?.kind === "nenda",
  };
}

/** The same split as the server's: your own organisation at edit, any other at administrate. */
function useUsersGate(): (companyId: string | null) => Gate {
  const me = useOptionalAuth()?.user ?? null;
  const access = useMemo(() => accessOf(me), [me]);
  const own = me?.access?.homeCompanyId ?? null;
  return useCallback(
    (companyId: string | null): Gate => {
      const ownPlace: Place | null =
        own && access && own !== access.nenda.id
          ? { kind: "company", id: own }
          : null;
      const target: Place | null = companyId
        ? { kind: "company", id: companyId }
        : null;
      const inOwn =
        ownPlace !== null &&
        target !== null &&
        (target.id === ownPlace.id ||
          (access !== null && isBelow(target, ownPlace, access.tree)));
      return {
        resource: "users",
        level: inOwn ? "edit" : "administrate",
        place: companyId ? { companyId } : "nenda",
      };
    },
    [own, access]
  );
}

function homeCompany(home: HomeChoice | null): string | null {
  return home?.kind === "company" ? home.companyId : null;
}

const EMPTY_DIRECTORY: AccessDirectory = {
  nendaCompanyId: "",
  companies: [],
  premises: [],
};

export default function UsersPage() {
  const usersGate = useUsersGate();
  const me = useOptionalAuth()?.user ?? null;
  const ownGate = usersGate(me?.access?.homeCompanyId ?? null);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [sentInvite, setSentInvite] = useState<{
    email: string;
    acceptUrl: string;
  } | null>(null);
  const [directory, setDirectory] = useState<AccessDirectory>(EMPTY_DIRECTORY);
  const [owners, setOwners] = useState<ContentOwnerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["users", "common"]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // `editing` is NOT cleared on close: the drawer keeps showing that user while
  // it slides out. Opening the editor for a row replaces it.
  const [editing, setEditing] = useState<UserRecord | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<UserRecord | null>(null);

  function openRowMenu(e: MouseEvent<HTMLElement>, u: UserRecord) {
    setMenuAnchor(e.currentTarget);
    setMenuUser(u);
  }
  function closeRowMenu() {
    setMenuAnchor(null);
    setMenuUser(null);
  }

  async function refresh() {
    setLoading(true);
    try {
      const [u, i, d, o] = await Promise.all([
        foyerApi.listUsers(),
        foyerApi.listInvites(),
        foyerApi.listAccessPlaces(),
        // Only used for the "(disabled)" note on a content-owner company - a
        // failure just means that note is missing.
        foyerApi.listContentOwners().catch(() => [] as ContentOwnerRecord[]),
      ]);
      setUsers(u);
      setInvites(i);
      setDirectory(d);
      setOwners(o);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function revokeInvite(id: string) {
    const email =
      invites.find((i) => i.id === id)?.email ?? t("usersPage.thisAddress");
    if (!confirm(t("usersPage.confirmRevokeInvite", { email }))) return;
    try {
      await foyerApi.revokeInvite(id);
      toast.success(t("usersPage.inviteRevoked", { email }));
      refresh();
    } catch (e) {
      toast.error(describeError(t, "revokeInvite", e, { email }));
    }
  }

  async function deleteUser(u: UserRecord) {
    if (!confirm(t("usersPage.confirmDeleteUser", { email: u.email }))) return;
    try {
      await foyerApi.deleteUser(u.id);
      toast.success(t("usersPage.userDeleted", { email: u.email }));
      refresh();
    } catch (e) {
      toast.error(describeError(t, "deleteUser", e, { email: u.email }));
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(t("usersPage.linkCopied")))
      .catch(() => toast.error(t("usersPage.linkCopyFailed")));
  }

  if (loading && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow={t("usersPage.eyebrow")}
          title={t("usersPage.title")}
        />
        <TableSkeleton rows={6} />
      </PageContainer>
    );
  }

  if (err && users.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{err}</Alert>
      </Box>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("usersPage.eyebrow")}
        title={t("usersPage.title")}
        actions={
          <PermissionGate {...ownGate}>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => setAddOpen(true)}
              data-testid="users-add"
            >
              {t("usersPage.addUser")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
              data-testid="users-invite"
            >
              {t("usersPage.inviteUser")}
            </Button>
          </PermissionGate>
        }
      />

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("common:labels.email")}</TableCell>
                <TableCell sx={{ width: 220 }}>
                  {t("usersPage.columns.homeOrganisation")}
                </TableCell>
                <TableCell>{t("common:labels.access")}</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box
                      sx={{
                        py: 5,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">
                        {t("usersPage.noUsers")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id} hover data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <HomeCell home={u.home} directory={directory} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={
                        u.grants.length ? "text.primary" : "text.secondary"
                      }
                      data-testid={`user-access-${u.email}`}
                    >
                      {u.grants.length
                        ? describeForm(
                            t,
                            formFromGrants(
                              u.grants,
                              placeOfHome(u.home, directory)
                            ),
                            directory
                          )
                        : t("usersPage.noGrants")}
                    </Typography>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <PermissionGate {...usersGate(homeCompany(u.home))}>
                      <IconButton
                        size="small"
                        aria-label={t("usersPage.actionsFor", {
                          email: u.email,
                        })}
                        onClick={(e) => openRowMenu(e, u)}
                        data-testid={`user-menu-${u.email}`}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            if (menuUser) {
              setEditing(menuUser);
              setEditOpen(true);
            }
            closeRowMenu();
          }}
          data-testid={menuUser ? `user-edit-${menuUser.email}` : undefined}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common:buttons.edit")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const u = menuUser;
            closeRowMenu();
            if (u) deleteUser(u);
          }}
          data-testid={menuUser ? `user-delete-${menuUser.email}` : undefined}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>{t("common:buttons.delete")}</ListItemText>
        </MenuItem>
      </Menu>

      <Typography variant="h6">{t("usersPage.pendingInvites")}</Typography>
      {sentInvite && (
        <Alert
          severity="success"
          onClose={() => setSentInvite(null)}
          data-testid="invite-sent"
          action={
            <Tooltip title={t("usersPage.copyInviteLink")}>
              <IconButton
                size="small"
                aria-label={t("usersPage.copyInviteLinkFor", {
                  email: sentInvite.email,
                })}
                onClick={() => copyToClipboard(sentInvite.acceptUrl)}
                data-testid="invite-sent-copy"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          {t("usersPage.inviteCreated", { email: sentInvite.email })}{" "}
          <Box component="span" sx={{ wordBreak: "break-all" }}>
            {sentInvite.acceptUrl}
          </Box>
        </Alert>
      )}
      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("common:labels.email")}</TableCell>
                <TableCell sx={{ width: 100 }}>
                  {t("common:labels.role")}
                </TableCell>
                <TableCell sx={{ width: 120 }}>
                  {t("common:labels.status")}
                </TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t("usersPage.noInvites")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {invites.map((i) => {
                return (
                  <TableRow key={i.id} hover>
                    <TableCell>{i.email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={roleLabel(t, i.role)} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`usersPage.inviteStatus.${i.status}`)}
                        color={
                          i.status === "pending"
                            ? "warning"
                            : i.status === "redeemed"
                              ? "success"
                              : "default"
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <PermissionGate {...usersGate(homeCompany(i.home))}>
                        <Tooltip title={t("usersPage.revokeInvite")}>
                          <IconButton
                            size="small"
                            aria-label={t("usersPage.revokeInviteFor", {
                              email: i.email,
                            })}
                            onClick={() => revokeInvite(i.id)}
                            data-testid={`invite-revoke-${i.email}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </PermissionGate>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <InviteDialog
        open={inviteOpen}
        directory={directory}
        owners={owners}
        onClose={() => setInviteOpen(false)}
        onCreated={(created) => {
          setInviteOpen(false);
          // The link stays on screen (it is the fallback if the email does not
          // arrive); the toast confirms the invite exists.
          toast.success(t("usersPage.inviteSent", { email: created.email }));
          setSentInvite({ email: created.email, acceptUrl: created.acceptUrl });
          refresh();
        }}
        onError={(msg) => toast.error(msg)}
      />

      <AddUserDialog
        open={addOpen}
        directory={directory}
        owners={owners}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setAddOpen(false);
          toast.success(t("usersPage.userCreated", { email: created.email }));
          refresh();
        }}
      />

      <UserEditDialog
        open={editOpen}
        user={editing}
        directory={directory}
        owners={owners}
        onClose={() => setEditOpen(false)}
        // Runs after the drawer has slid out.
        onSaved={() => {
          setEditOpen(false);
          toast.success(
            editing
              ? t("usersPage.userUpdated", { email: editing.email })
              : t("usersPage.userUpdatedUnnamed")
          );
          refresh();
        }}
      />
    </PageContainer>
  );
}

function HomeCell({
  home,
  directory,
}: {
  home: HomeChoice | null;
  directory: AccessDirectory;
}) {
  const { t } = useTranslation("users");
  if (!home) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={t("usersPage.home.notChosen")}
      />
    );
  }
  if (home.kind === "nenda") {
    return (
      <Chip
        size="small"
        color="primary"
        label={t("usersPage.home.nendasOwn")}
      />
    );
  }
  return (
    <Typography variant="body2">
      {placeName(directory, { kind: "company", id: home.companyId })}
    </Typography>
  );
}

/** The home organisation and grants, as the add and edit forms share them. */
function AccessFields({
  who,
  home,
  onHome,
  form,
  onForm,
  directory,
  owners,
  note,
  onNote,
  disabled,
  patientDataOnly,
}: {
  who: string;
  home: HomeChoice | null;
  onHome: (home: HomeChoice) => void;
  form: GrantForm;
  onForm: (form: GrantForm) => void;
  directory: AccessDirectory;
  owners: readonly ContentOwnerRecord[];
  note: string | null;
  onNote: (note: string | null) => void;
  disabled?: boolean;
  patientDataOnly?: boolean;
}) {
  const { t } = useTranslation("users");
  const caller = useCaller();
  return (
    <>
      <HomePicker
        value={home}
        onChange={(next) => {
          const fitted = fitFormToHome(
            t,
            form,
            kindOf(next, directory),
            directory
          );
          onHome(next);
          onForm(fitted.form);
          onNote(homeChangeNote(t, fitted.removed));
        }}
        directory={directory}
        owners={owners}
        canChooseNenda={caller.isNendaAdmin}
        who={who || t("usersPage.thisUser")}
        disabled={disabled || patientDataOnly}
      />
      <HomeChangeNote note={note} onClose={() => onNote(null)} />
      <GrantEditor
        form={form}
        onChange={onForm}
        directory={directory}
        home={home}
        kind={kindOf(home, directory)}
        callerIsNendaStaff={caller.isNendaStaff}
        disabled={disabled}
        patientDataOnly={patientDataOnly}
      />
    </>
  );
}

function HomeChangeNote({
  note,
  onClose,
}: {
  note: string | null;
  onClose: () => void;
}) {
  if (!note) return null;
  return (
    <Alert severity="warning" onClose={onClose} data-testid="home-change-note">
      {note}
    </Alert>
  );
}

/** The first reason a save is disabled, beside its button. */
function SaveBlocked({
  problems,
  testId,
}: {
  problems: readonly string[];
  testId: string;
}) {
  const { t } = useTranslation("users");
  if (problems.length === 0) return null;
  return (
    <Typography
      variant="caption"
      color="error"
      sx={{ mr: "auto", pl: 1 }}
      data-testid={testId}
    >
      {problems[0]}
      {problems.length > 1
        ? t("usersPage.andMore", { count: problems.length - 1 })
        : ""}
    </Typography>
  );
}

interface InviteDialogProps {
  open: boolean;
  directory: AccessDirectory;
  owners: readonly ContentOwnerRecord[];
  onClose: () => void;
  onCreated: (i: CreatedInviteRecord) => void;
  onError: (msg: string) => void;
}

/**
 * An invite gives one role, on the home organisation or on some of its
 * premises. Personal levels and exceptions are set once the user has joined.
 */
function InviteDialog({
  open,
  directory,
  owners,
  onClose,
  onCreated,
  onError,
}: InviteDialogProps) {
  const { t } = useTranslation(["users", "common"]);
  const caller = useCaller();
  const [email, setEmail] = useState("");
  const [home, setHome] = useState<HomeChoice | null>(null);
  const [role, setRole] = useState<RoleName | null>(null);
  const [premises, setPremises] = useState<Place[]>([]);
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setHome(null);
      setRole(null);
      setPremises([]);
      setEmailError(null);
      setRefusal(null);
      setNote(null);
    }
  }, [open]);

  function changeHome(next: HomeChoice) {
    const nextKind = kindOf(next, directory);
    const removed: string[] = [];
    if (roleTooHigh(role, nextKind)) {
      removed.push(t("formChecks.removedRole", { role: roleLabel(t, role) }));
      setRole(null);
    }
    const allowed =
      next.kind === "company"
        ? placeOptions(t, directory, next, nextKind, false)
        : [];
    const kept = premises.filter((p) =>
      allowed.some((o) => samePlace(o.place, p))
    );
    for (const p of premises) {
      if (!kept.includes(p)) {
        removed.push(
          t("formChecks.removedPremise", {
            premise: placeLabel(t, directory, p),
          })
        );
      }
    }
    setHome(next);
    setPremises(kept);
    setNote(homeChangeNote(t, removed));
  }

  const kind = kindOf(home, directory);
  const problems = inviteProblems(t, role, kind);
  const premiseOptions = useMemo(
    () =>
      placeOptions(t, directory, home, kind, false).filter(
        (o) => o.place.kind === "premise"
      ),
    [t, directory, home, kind]
  );

  async function submit() {
    if (!isEmail(email)) {
      setEmailError(t("usersPage.inviteDialog.invalidEmail"));
      return;
    }
    if (!home || !role) return;
    setBusy(true);
    setEmailError(null);
    setRefusal(null);
    try {
      const created = await foyerApi.createInvite({
        email,
        home: toHomeInput(home),
        role,
        premises: premises.map((p) => p.id),
      });
      onCreated(created);
    } catch (e) {
      const code = errorCode(e);
      if (code === "user_already_exists" || code === "invite_already_pending") {
        setEmailError(friendlyError(t, e));
      } else if (code) {
        setRefusal(refusalMessage(t, "sendInvite", e, { email }));
      } else {
        onError(describeError(t, "sendInvite", e, { email }));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("usersPage.inviteDialog.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("common:labels.email")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            fullWidth
            required
            size="small"
            autoFocus
            error={!!emailError}
            helperText={emailError ?? undefined}
            inputProps={{ "data-testid": "invite-email" }}
          />
          <HomePicker
            value={home}
            onChange={changeHome}
            directory={directory}
            owners={owners}
            canChooseNenda={caller.isNendaAdmin}
            who={email || t("usersPage.thisUser")}
          />
          <HomeChangeNote note={note} onClose={() => setNote(null)} />
          <RoleSelect
            value={role}
            onChange={setRole}
            kind={kind}
            allowNone={false}
            testId="invite-role"
          />
          {home?.kind === "company" && (
            <PlacesField
              options={premiseOptions}
              directory={directory}
              value={premises}
              onChange={setPremises}
              label={t("usersPage.inviteDialog.onlyThesePremises")}
              testId="invite-premises"
            />
          )}
          {problems.length > 0 && (
            <Alert severity="error" data-testid="invite-problems">
              {problems.map((p) => (
                <div key={p}>{p}</div>
              ))}
            </Alert>
          )}
          {refusal && (
            <Alert severity="error" data-testid="invite-refusal">
              {refusal}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <SaveBlocked problems={problems} testId="invite-blocked" />
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={
            busy || !isEmail(email) || !home || !role || problems.length > 0
          }
          onClick={submit}
          data-testid="invite-submit"
        >
          {busy
            ? t("common:states.sending")
            : t("usersPage.inviteDialog.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddUserDialog({
  open,
  directory,
  owners,
  onClose,
  onCreated,
}: {
  open: boolean;
  directory: AccessDirectory;
  owners: readonly ContentOwnerRecord[];
  onClose: () => void;
  onCreated: (u: UserRecord) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [home, setHome] = useState<HomeChoice | null>(null);
  const [form, setForm] = useState<GrantForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const problems = formProblems(
    t,
    form,
    kindOf(home, directory),
    directory,
    home
  );

  useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setHome(null);
      setForm(EMPTY_FORM);
      setRefusal(null);
      setNote(null);
    }
  }, [open]);

  async function submit() {
    if (!home) return;
    setBusy(true);
    setRefusal(null);
    try {
      const created = await foyerApi.createUser({
        email,
        password,
        home: toHomeInput(home),
        grants: toWire(grantsFromForm(form)),
      });
      onCreated(created);
    } catch (e) {
      setRefusal(refusalMessage(t, "createUser", e, { email }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("usersPage.addDialog.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("common:labels.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            required
            autoFocus
            inputProps={{ "data-testid": "add-email" }}
          />
          <TextField
            label={t("common:labels.password")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            required
            helperText={t("usersPage.addDialog.passwordHelp")}
            inputProps={{ "data-testid": "add-password" }}
          />
          <AccessFields
            who={email}
            home={home}
            onHome={setHome}
            form={form}
            onForm={setForm}
            directory={directory}
            owners={owners}
            note={note}
            onNote={setNote}
          />
          {refusal && (
            <Alert severity="error" data-testid="user-refusal">
              {refusal}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <SaveBlocked problems={problems} testId="add-blocked" />
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={
            busy ||
            !isEmail(email) ||
            password.length < 8 ||
            !home ||
            problems.length > 0
          }
          onClick={submit}
          data-testid="add-submit"
        >
          {busy ? t("common:states.creating") : t("usersPage.addDialog.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UserEditDialog({
  open,
  user,
  directory,
  owners,
  onClose,
  onSaved,
}: {
  open: boolean;
  user: UserRecord | null;
  directory: AccessDirectory;
  owners: readonly ContentOwnerRecord[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [home, setHome] = useState<HomeChoice | null>(null);
  const [form, setForm] = useState<GrantForm>(EMPTY_FORM);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const caller = useCaller();
  const saveGate = useUsersGate()(homeCompany(user?.home ?? null));

  // Seeded on every open, not only when the user changes: the page keeps the
  // last-edited user after closing, so reopening the same one must re-seed.
  useEffect(() => {
    if (!user || !open) return;
    setHome(user.home);
    setForm(formFromGrants(user.grants, placeOfHome(user.home, directory)));
    setPassword("");
    setRefusal(null);
    setNote(null);
  }, [user, open, directory]);

  if (!user) return null;
  const self = caller.isSelf(user.email);
  const ownPatientData = self && caller.isNendaAdmin;
  const problems =
    self && !ownPatientData
      ? []
      : formProblems(t, form, kindOf(home, directory), directory, home);

  async function submit(dismiss: Dismiss) {
    if (!user) return;
    setBusy(true);
    setRefusal(null);
    try {
      if (ownPatientData) {
        await foyerApi.updateUser(user.id, {
          grants: toWire(grantsFromForm(form)),
          ...(password ? { password } : {}),
        });
      } else if (self) {
        if (password) await foyerApi.updateUser(user.id, { password });
      } else {
        if (!home) {
          setRefusal(t("usersPage.editDialog.chooseHome"));
          return;
        }
        await foyerApi.updateUser(user.id, {
          home: toHomeInput(home),
          grants: toWire(grantsFromForm(form)),
          ...(password ? { password } : {}),
        });
      }
      // Slide the drawer away first; the page then toasts and refreshes.
      dismiss(() => onSaved());
    } catch (e) {
      setRefusal(refusalMessage(t, "saveUser", e, { email: user.email }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <EditDrawer
      open={open}
      onClose={onClose}
      title={t("usersPage.editDialog.title", { email: user.email })}
      busy={busy}
      actions={(dismiss) => (
        <>
          <SaveBlocked problems={problems} testId="user-save-blocked" />
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate {...saveGate} deniedProps={{ disabled: true }}>
            <Button
              variant="contained"
              disabled={busy || problems.length > 0}
              onClick={() => void submit(dismiss)}
              data-testid="user-save"
            >
              {busy ? t("common:states.saving") : t("common:buttons.save")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {self && (
          <Alert severity="info" data-testid="user-self-note">
            {ownPatientData
              ? t("usersPage.editDialog.selfPatientData")
              : t("usersPage.editDialog.selfLocked")}
          </Alert>
        )}
        {!self && user.grants.length === 0 && (
          <Alert severity="info" data-testid="user-no-grants">
            {t("usersPage.editDialog.noGrantsNoRole")}
          </Alert>
        )}
        <AccessFields
          who={user.email}
          home={home}
          onHome={setHome}
          form={form}
          onForm={setForm}
          directory={directory}
          owners={owners}
          note={note}
          onNote={setNote}
          disabled={self && !ownPatientData}
          patientDataOnly={ownPatientData}
        />
        {refusal && (
          <Alert severity="error" data-testid="user-refusal">
            {refusal}
          </Alert>
        )}
        <TextField
          label={t("usersPage.editDialog.resetPassword")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          helperText={t("usersPage.editDialog.keepPassword")}
        />
      </Stack>
    </EditDrawer>
  );
}
